/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jun 20 2023
 **/

import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { timer } from "rxjs";

import { useSpace } from "@/frameworks/core/API";
import {
	Button,
	Column,
	Divider,
	Icon,
	Radio,
	TextField,
	Tooltip,
	Typography,
	useToast
} from "@/frameworks/designStuff/components";
import { Row } from "@/frameworks/designStuff/components";
import { UserChip, UserItem } from "@/frameworks/core/components";
import { Maybe } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { useChatRoomActions } from "@/frameworks/core/chat";

import * as S from "./emotion";

interface CreateSpaceChannelProps extends React.HTMLAttributes<HTMLDivElement> {
	onHide: () => void;
	onCreateSpaceSuccess?: (id: string) => void;
	spaceID: string;
	channelUserList: {
		name: string;
		src: string | undefined;
		userID: string;
	}[];
}

type ValidateUserEmail = {
	valid: boolean;
	name: string;
	userID: string;
	email: string;
	profileImagePath?: string;
};

export default function CreateSpaceChannel(props: CreateSpaceChannelProps) {
	const titleInputRef = useRef<HTMLInputElement>(null);
	const [spaceUserList, setSpaceUserList] = useState<WebServices.SpaceUser.List.Data>([]);
	const spaceAPI = useSpace();
	const toast = useToast();
	const chatRoomActions = useChatRoomActions();
	const [searchResult, setSearchResult] = useState<WebServices.SpaceUser.List.Data>([]); // 검색어 결과 리스트
	const [keyword, setKeyword] = useState("");
	const [inviteList, setInviteList] = useState<ValidateUserEmail[]>([]);
	const [formData, setFormData] = useState<WebServices.SpaceChat.Create.Request>({
		name: "",
		private: false,
		spaceId: props.spaceID,
		userIds: []
	});
	const [showError, setShowError] = useState(false);

	useEffect(() => {
		spaceAPI.user.list.execute({ spaceId: props.spaceID }).then(({ data }) => {
			if (data) {
				setSpaceUserList(data);
			}
		});
	}, []);

	// 첫 렌더링시 Input 포커스가 안되는 문제 해결
	useEffect(() => {
		titleInputRef.current?.focus();
	}, []);

	const onSubmit = () => {
		if (formData.name.trim().length === 0) {
			setShowError(true);
			return;
		}
		const userIDs = inviteList.filter((user) => user.userID).map((user) => user.userID);
		if (formData.private && userIDs.length === 0) {
			toast.show({ type: "error", message: _T("채널에 추가할 멤버를 선택해주세요.") });
			return;
		}
		chatRoomActions.spaceChat
			.create({
				userIDs,
				spaceID: props.spaceID,
				name: formData.name,
				isPrivate: formData.private
			})
			.then((res) => {
				if (!res.data) return;

				chatRoomActions.common.open({ channelID: res.data?.id, type: "Space", spaceID: props.spaceID });
				if (props?.onCreateSpaceSuccess) props.onCreateSpaceSuccess(res.data?.id);
			});

		props.onHide();
	};

	const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
		const changeValue = ev.currentTarget.dataset["key"];
		const old = { ...formData };
		const value = ev.target.value;
		switch (changeValue) {
			case "name": {
				old.name = value;
				if (value.trim().length > 0) {
					setShowError(false);
				}
				break;
			}
			case "scope": {
				old.private = value === "private";
				break;
			}
		}
		setFormData(old);
	};

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		const type = e.currentTarget.dataset["type"];

		switch (type) {
			case "cancel":
				props.onHide();
				break;
			case "confirm":
				onSubmit();
				break;
		}
	};

	const handleChangeField = (ev: ChangeEvent<HTMLInputElement>) => {
		const value = ev.target.value;
		setKeyword(value);
	};

	useEffect(() => {
		const result = spaceUserList.filter((user) => user.name.includes(keyword) || user.username.includes(keyword));
		setSearchResult(result);
	}, [keyword]);

	const handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
		const target = evt.target as HTMLInputElement;
		if (evt.key === "Enter" && !evt.shiftKey && evt.nativeEvent.isComposing === false) {
			const addUser = searchResult[0];
			if (addUser) {
				if (inviteList.map((e) => e.userID).includes(addUser.id)) {
					setInviteList(inviteList.filter((e) => e.name !== addUser.name));
				} else {
					setInviteList((prev) => [
						...prev,
						{ name: addUser.name, email: addUser.username, userID: addUser.id, valid: true }
					]);
				}
			} else {
				setInviteList((prev) => [...prev, { name: target.value, email: "", userID: "", valid: false }]);
			}
			timer(0).subscribe(() => {
				target.value = "";
				setKeyword("");
			});
		}

		if (evt.key === "Backspace") {
			if (target.value.length > 0) return;
			const newArr = [...inviteList];
			newArr.pop();
			setInviteList(newArr);
		}
	};

	const handleUserClick = (user: WebServices.SpaceUser.List.Data[number]) => {
		if (inviteList.map((e) => e.userID).includes(user.id)) {
			// 이미 초대된 사람인 경우
			setInviteList(inviteList.filter((e) => e.name !== user.name));
		} else {
			setInviteList((prev) => [...prev, { name: user.name, email: user.username, userID: user.id, valid: true }]);
		}
	};

	const handleMatchData = (data: WebServices.SpaceUser.List.Data) => {
		if (!data) return [];

		return data.filter((item) => {
			if (item.username.indexOf(keyword) !== -1) return true;
			else if (item.name.indexOf(keyword) !== -1) return true;
			else false;
		});
	};

	const filterByEmailAndName = (userList: WebServices.SpaceUser.List.Data, value: string) => {
		if (value === "") return userList;
		return userList.filter((user) => user.name.includes(value) || user.username.includes(value));
	};

	return (
		<S.Wrapper horizonAlign="left">
			<S.Header horizonAlign="distribute" verticalAlign="center">
				<Typography typoSize="Head6">{_T("스페이스 채널 만들기")}</Typography>
				<S.HeaderIconList>
					<Button.Icon name="Close24" variant="text" size="large" onClick={() => props.onHide()} />
				</S.HeaderIconList>
			</S.Header>
			<S.Body>
				<Divider type="line" />
				<S.InnerBody gap={20}>
					<S.ChannelTitle gap={12}>
						<Row horizonAlign="distribute" style={{ width: "100%" }}>
							<Row gap={3}>
								<Typography typoSize="Body4" typoColor="neutral_60_100">
									{_T("채널 이름")}
								</Typography>
								<Typography typoSize="Body4" typoColor="error_50_100">
									{_T("(필수)")}
								</Typography>
							</Row>
							<Typography typoSize="Body4" typoColor="neutral_80_100">
								{`${formData ? formData?.name.length : 0} / 50`}
							</Typography>
						</Row>
						<TextField
							ref={titleInputRef}
							onChange={handleChange}
							data-key="name"
							maxLength={50}
							placeholder={_T("채널 이름을 입력해주세요")}
							error={showError && !formData?.name}
							help={showError && !formData?.name ? _T("필수로 입력해주세요") : ""}
						/>
					</S.ChannelTitle>
					<Column>
						<S.FieldLabel>
							<Typography typoSize="Body4" typoColor={"neutral_60_100"}>
								{_T("채널 속성")}
							</Typography>
						</S.FieldLabel>
						<Row gap={20}>
							<Radio
								id="public-channel"
								onChange={handleChange}
								name="channelRadio"
								data-key="scope"
								checked={!formData.private}
								value={"public"}
							>
								<Typography typoSize="Body3">{_T("공개")}</Typography>
							</Radio>
							<Radio
								id="private-channel"
								onChange={handleChange}
								name="channelRadio"
								data-key="scope"
								checked={formData.private}
								value={"private"}
							>
								<Typography typoSize="Body3">{_T("비공개")}</Typography>
							</Radio>
						</Row>
						<Typography typoSize="Body4" typoColor={"neutral_60_100"}>
							{_T(
								"비공개 채널은 추가한 멤버만 사용하며, 공개 채널은 스페이스 참여자 누구나 사용할 수 있습니다."
							)}
						</Typography>
					</Column>
					{formData.private ? (
						<Column style={{ width: "100%" }} gap={8}>
							<TextField.Tag
								label={_T("채널 멤버")}
								onChange={handleChangeField}
								onKeyDown={handleKeyDown}
								minHeight="40px"
								help={_T("이름과 이메일로 검색해주세요.")}
								tags={(() => {
									return inviteList.map((chipData) => (
										<Maybe test={chipData?.valid} key={`${chipData?.name}`}>
											<UserChip
												userID={chipData?.userID || chipData.name}
												key={chipData?.userID}
												isValid={true}
												handleClose={() => {
													setInviteList((prev) =>
														prev.filter((e) => e.name !== chipData.name)
													);
												}}
											/>
											<Maybe.Fallback>
												<Tooltip message={_T("다시 확인해주세요.")} hasIcon={true}>
													<UserChip
														userID={chipData?.userID || chipData?.name}
														key={chipData.name}
														isValid={false}
														handleClose={() => {
															setInviteList(
																inviteList?.filter((e) => e.name !== chipData.name)
															);
														}}
													/>
												</Tooltip>
											</Maybe.Fallback>
										</Maybe>
									));
								})()}
							/>
							<S.UserList>
								{handleMatchData(filterByEmailAndName(spaceUserList, keyword)).map((user, index) => {
									if (user.id === Configure.userID) return;
									return (
										<li key={index} css={{ width: "100%" }}>
											<UserItem
												id={user.id}
												data-email={user.username}
												onClick={() => handleUserClick(user)}
											>
												<Maybe test={inviteList.map((e) => e.email).includes(user.username)}>
													<Row
														css={{
															width: "100%"
														}}
														horizonAlign="right"
													>
														<S.CheckIconWrap>
															<Icon name="Check24" size="24" />
														</S.CheckIconWrap>
													</Row>
												</Maybe>
											</UserItem>
										</li>
									);
								})}
							</S.UserList>
						</Column>
					) : null}
				</S.InnerBody>
			</S.Body>
			<S.Footer horizonAlign="right" verticalAlign="center">
				<Button.Group>
					<Button filled="secondary" onClick={handleClick} data-type="cancel">
						{_T("취소")}
					</Button>
					<Button onClick={handleClick} data-type="confirm">
						{_T("생성")}
					</Button>
				</Button.Group>
			</S.Footer>
		</S.Wrapper>
	);
}
